import type {
	CreateHomeWaitlistRequest,
	HomeEventDto,
	HomeWaitlistDto,
	ValidateHomeEventAccessRequest,
} from '@/types';
import api from './base-api';

const HOME_EVENT_TAGS = {
	featured: 'HOME_FEATURED',
	crowdFavorites: 'HOME_CROWD_FAVORITES',
} as const;

const getHomeEventTags = (events: HomeEventDto[] | undefined, listId: string) => [
	{ type: 'Event' as const, id: listId },
	...(events?.map(({ id }) => ({ type: 'Event' as const, id })) ?? []),
];

const markEventAsWaitlisted = (events: HomeEventDto[], eventId: string): void => {
	const event = events.find(({ id }) => id === eventId);

	if (event) {
		event.isInWaitlist = true;
		event.subscriber_count = (event.subscriber_count ?? 0) + 1;
	}
};

const eventsApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getPublicFeaturedEvents: builder.query<HomeEventDto[], void>({
			query: () => ({ url: 'events/featured' }),
			providesTags: (result) => getHomeEventTags(result, HOME_EVENT_TAGS.featured),
			extraOptions: { skipAuth: true },
		}),
		getFeaturedEventsForUser: builder.query<HomeEventDto[], void>({
			query: () => ({ url: 'events/featured/me' }),
			providesTags: (result) => getHomeEventTags(result, HOME_EVENT_TAGS.featured),
		}),
		getPublicCrowdFavoriteEvents: builder.query<HomeEventDto[], void>({
			query: () => ({ url: 'events/crowd-favorites' }),
			providesTags: (result) => getHomeEventTags(result, HOME_EVENT_TAGS.crowdFavorites),
			extraOptions: { skipAuth: true },
		}),
		getCrowdFavoriteEventsForUser: builder.query<HomeEventDto[], void>({
			query: () => ({ url: 'events/crowd-favorites/me' }),
			providesTags: (result) => getHomeEventTags(result, HOME_EVENT_TAGS.crowdFavorites),
		}),
		createHomeWaitlist: builder.mutation<HomeWaitlistDto, CreateHomeWaitlistRequest>({
			query: (body) => ({
				url: 'waitlists',
				method: 'POST',
				body,
			}),
			async onQueryStarted({ event_id: eventId }, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled;

					dispatch(
						eventsApi.util.updateQueryData('getFeaturedEventsForUser', undefined, (events) => {
							markEventAsWaitlisted(events, eventId);
						})
					);
					dispatch(
						eventsApi.util.updateQueryData('getCrowdFavoriteEventsForUser', undefined, (events) => {
							markEventAsWaitlisted(events, eventId);
						})
					);
				} catch {
					// The mutation result already exposes the request error to its caller.
				}
			},
		}),
		validateHomeEventAccess: builder.mutation<void, ValidateHomeEventAccessRequest>({
			query: (body) => ({
				url: 'events/join',
				method: 'POST',
				body,
			}),
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetPublicFeaturedEventsQuery,
	useGetFeaturedEventsForUserQuery,
	useGetPublicCrowdFavoriteEventsQuery,
	useGetCrowdFavoriteEventsForUserQuery,
	useCreateHomeWaitlistMutation,
	useValidateHomeEventAccessMutation,
} = eventsApi;

export default eventsApi;
